import PropTypes from "prop-types";
import Button from "../../../components/Button";
import ProgressBar from "../../../components/ProgressBar";
import { Link, useNavigate } from "react-router-dom";
import { useUserContext } from "../../../context/userContext";
import { useEffect, useState } from "react";
import { useDrawerContext } from "../../../context/drawerContext";
import CloseIcon from "/public/images/icons/close.svg?react";
import EmailIcon from "/public/images/icons/email.svg?react";
import PhoneIcon from "/public/images/icons/phone.svg?react";
import fetchWithRefresh from "../../../utils/fetchWithRefresh";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { LogErrors } from "../../../utils/LogErrors";

export default function Step1({ band, emergencyContacts, medicalHistory, allergies, medications }) {
  const navigate = useNavigate();
  const { user, logout, updateTokens } = useUserContext();
  const { setDrawerContent, setOpen } = useDrawerContext();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUserProfile = async () => {
    await Sentry.startSpan(
      {
        name: "Fetch User Profile",
        op: "http.request",
      },
      async (span) => {
        try {
          const response = await fetchWithRefresh(
            `${import.meta.env.VITE_API_URL}Profile/FetchUserProfile?participantid=${band?.ParticipantId}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${user?.token}`,
              },
            },
            logout,
            updateTokens,
            (message) => { toast.info(message); }
          );

          // Set HTTP response status in the span
          span.setAttribute("http.status_code", response.status);

          if (response.status === 200) {
            const data = await response.json();
            setUserProfile(data);
          } else {
            span.setStatus("error"); // Mark as error
          }
        } catch (error) {
          span.setStatus("error");
          Sentry.captureException(error);
          LogErrors(user, logout, updateTokens, error);
        } finally {
          span.end(); // Ensure span ends properly
        }
      }
    );
  };

  useEffect(() => {
    fetchUserProfile();
  }, [band]);

  useEffect(() => {
    if (userProfile) {
      setLoading(false);
    }
  }, [userProfile]);

  const notMyBandDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full not-your-band">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            NOT YOUR BAND?
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-4 justify-stretch">
          <h3 className="text-[24px]">Please contact us</h3>
          <p className="text-sportsDarkGrey">
            Let us know the details of the band you've received and we'll fix this for you.
            Our offices are open Monday - Friday between 09:00 - 16:30.
          </p>
        </div>
      </div>
    );
  };

  const contactDrawer = () => {
    setOpen(true);
    setDrawerContent(
      <div className="w-full">
        <div className="flex items-center justify-between w-full">
          <br />
          <h2 className="leading-5 tracking-widest uppercase text-[12px]">
            Contact
          </h2>
          <CloseIcon className='cursor-pointer' onClick={() => setOpen(false)} />
        </div>
        <div className="mt-6 flex flex-col gap-4 justify-stretch">
          <h3 className="text-[24px]">Need some assistance?</h3>
          <p className=" text-sportsDarkGrey">
            We&apos;re happy to assist with any query. Our offices are open
            Monday - Friday between 09:00 - 16:30.
          </p>
          <Link className="flex items-center gap-2" to="tel:0800 999 3669">
            <PhoneIcon />
            <Button status="nobg" variant="tertiary" onClick={() => setOpen(false)}>
              0800 999 3669
            </Button>
          </Link>
          <Link className="flex items-center gap-2" to="mailto:hello@sportsguardian.com">
            <EmailIcon />
            <Button status="nobg" variant="tertiary" onClick={() => setOpen(false)}>
              hello@sportsguardian.com
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-svh flex flex-col justify-stretch p-[24px] gap-6">
      {loading ? (
        <div className="flex justify-center items-center h-full">
          <div className="loading loading-spinner loading-lg">Loading...</div>
        </div>
      ) : (
        <>
          <ProgressBar step={1} />
          <div>
            <span className="tracking-[0.25em] text-[12px] uppercase font-semibold">
              Step 1
            </span>
            {userProfile?.UserAccountId === user?.id ? (
              <h2>You scanned this band, is this yours?</h2>
            ) : (
              <h2>This band isn’t linked to your account</h2>
            )}
          </div>
          <div className="w-full flex flex-col gap-6 items-center justify-center">
            <img src={band?.BandImageURL} alt="band" className="max-w-[210px]" />
            <h4 className="text-sportsDarkGrey">{band?.OrderItemParticipantName || band?.ParticipantName}</h4>
            <h4 className="text-sportsDarkGrey">
              ICE: {band?.OrderItemICENumber || (userProfile?.PrimaryIceNumber || "No ICE contact")}
            </h4>
            <h4 className="text-sportsDarkGrey">
              {band?.OrderItemMedical || userProfile?.PrimaryMedicalCondition || "No medical conditions"}
            </h4>
          </div>

          {userProfile?.UserAccountId === user?.id ? (
            <>
              <Button
                onClick={() =>
                  navigate("/add-band/2", {
                    state: {
                      band: band,
                      profile: userProfile,
                      emergencyContacts: emergencyContacts,
                      medicalHistory: medicalHistory,
                      allergies: allergies,
                      medications: medications,
                    },
                  })
                }
              >
                Confirm & Continue
              </Button>

              <Button status="nobg" variant="tertiary" onClick={notMyBandDrawer}>
                No, That&apos;s Not My Band
              </Button>
            </>
          ) : (
            <>
              <Button onClick={contactDrawer}>Contact Support</Button>
              <Button variant="quaternary" onClick={() => navigate("/")}>
                Cancel
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}

Step1.propTypes = {
  band: PropTypes.shape({
    ParticipantName: PropTypes.string,
    BandColour: PropTypes.string,
  }).isRequired,
};